/**
 * FE#128 — calendar/scheduled-messages sidebar search.
 *
 * The sidebar search used to match a scheduled MESSAGE only by its body text,
 * so typing a recipient's (or group's) name found nothing even though the row
 * shows that name. This predicate also matches a message against its recipient
 * names (`contactNames`, sourced from the message's `contactList`), so a search
 * by contact/group name surfaces the scheduled items sent to them.
 *
 * Campaign matching (template name / message) is unchanged.
 *
 * `normalizedToken` is expected to be already trimmed + lower-cased by the
 * caller; an empty token matches everything.
 */

export interface SidebarSearchableMessage {
    type: 'message';
    message?: string;
    contactNames?: string[];
}

export interface SidebarSearchableCampaign {
    type: 'campaign';
    templateName?: string;
    templateMessage?: string;
}

export type SidebarSearchableEntity = SidebarSearchableMessage | SidebarSearchableCampaign;

export function entityMatchesSidebarSearch(
    entity: SidebarSearchableEntity,
    normalizedToken: string,
): boolean {
    if (!normalizedToken) return true;

    if (entity.type === 'message') {
        if ((entity.message || '').toLowerCase().includes(normalizedToken)) return true;
        return (entity.contactNames || []).some(
            name => (name || '').toLowerCase().includes(normalizedToken),
        );
    }

    const templateName = (entity.templateName || '').toLowerCase();
    const templateMessage = (entity.templateMessage || '').toLowerCase();
    return templateName.includes(normalizedToken) || templateMessage.includes(normalizedToken);
}
